import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { calcMetabolicScore, type MetabolicScoreResult, type MetabolicSubScore } from '../lib/metabolicScore';
import { iconForPillarLabel } from '../lib/statusIcons';
import ScoreRing from './ScoreRing';
import InfoTooltip from './InfoTooltip';

interface Props {
  bodyFat: number | null;
  bmi: number | null;
  vo2Max: number | null;
  /** Lab values — find insulin, HbA1c, glucose, HDL, triglycerides */
  labs: Array<{ marker: string; value: number }>;
  /**
   * Biological sex — drives the body-fat / HDL / VO2max reference ranges
   * inside calcMetabolicScore. Defaults to 'male' so existing callers
   * keep working; new callers should pass `userSex` from
   * useDashboardData (see Dashboard.tsx).
   */
  sex?: 'male' | 'female';
}

/** Map status to pillar-style label for icon lookup. */
function statusToLabel(status: string): string {
  if (status === 'optimal') return 'Excellent';
  if (status === 'good') return 'Good';
  if (status === 'borderline') return 'Fair';
  return 'Needs Attention';
}

/** Compact sub-score mini-card. */
function SubScoreCard({ sub }: { sub: MetabolicSubScore }) {
  const label = statusToLabel(sub.status);
  const icon = iconForPillarLabel(label);
  return (
    <div className="bg-gray-50 dark:bg-dark-card/60 rounded-lg px-2.5 py-2 flex flex-col items-center text-center">
      <p className="text-[9px] text-arete-muted dark:text-gray-400 font-medium leading-tight truncate w-full">{sub.name}</p>
      <p className="text-sm font-bold text-arete-navy dark:text-gray-100 mt-0.5">{sub.score}/{sub.maxScore}</p>
      <span className="text-[9px] font-semibold mt-0.5" style={{ color: sub.status === 'optimal' ? '#22C55E' : sub.status === 'good' ? '#22C55E' : sub.status === 'borderline' ? '#F59E0B' : '#EF4444' }}>
        {icon} {label}
      </span>
    </div>
  );
}

/**
 * Compact metabolic health card for the Dashboard Overview.
 * Shows composite score + 4 sub-component mini-cards + insight sentence.
 */
export default React.memo(function DashboardMetabolicCard({ bodyFat, bmi, vo2Max, labs, sex = 'male' }: Props) {
  const navigate = useNavigate();

  const result: MetabolicScoreResult = useMemo(() => {
    const findLab = (name: string) => labs.find(l => l.marker === name)?.value ?? null;
    return calcMetabolicScore({
      bodyFatPercent: bodyFat, bmi, vo2max: vo2Max,
      fastingInsulin: findLab('Insulin'),
      hba1c: findLab('HbA1c'),
      fastingGlucose: findLab('Glucose'),
      hdl: findLab('HDL'),
      triglycerides: findLab('Triglycerides'),
      sex,
    });
  }, [bodyFat, bmi, vo2Max, labs, sex]);

  if (result.subScores.length === 0) return null;

  const scoreColor = result.totalScore >= 80 ? '#00A896' : result.totalScore >= 60 ? '#22C55E' : result.totalScore >= 40 ? '#F59E0B' : '#EF4444';

  return (
    <button
      onClick={() => navigate('/metabolic')}
      className="card text-left w-full hover:shadow-card-hover transition-shadow"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="section-label">Metabolic Health</h3>
          <span onClick={e => e.stopPropagation()}>
            <InfoTooltip
              text="Metabolic health is a leading indicator for mental health. Insulin resistance, inflammation, and low fitness are linked to depression risk (JAMA 2024)."
              ariaLabel="Metabolic health info"
            />
          </span>
        </div>
        <ScoreRing score={result.totalScore} label="" color={scoreColor} compact />
      </div>

      {/* Sub-score grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
        {result.subScores.map(sub => (
          <SubScoreCard key={sub.name} sub={sub} />
        ))}
      </div>

      {/* Data completeness */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] text-arete-muted dark:text-gray-500">
          {result.subScores.reduce((n, s) => n + s.metrics.length, 0)} metrics tracked
          {result.dataCompleteness < 1 ? ` · ${Math.round(result.dataCompleteness * 100)}% complete` : ''}
        </span>
      </div>

      {/* Insight */}
      <p className="text-[11px] text-arete-muted dark:text-gray-400 leading-relaxed italic">
        "{result.insight}"
      </p>
    </button>
  );
});
